import type { Note, Notebook, Tag } from "@engram/core";
import { parseTagIds } from "@engram/core/id";
import DOMPurify from "dompurify";
import {
  Book, ChevronLeft, Download, Home, Redo2, Undo2, ChevronRight, ChevronsDownUp, ChevronsLeft, ChevronsRight, Copy, CopyPlus, FolderInput, History, Info, Link2, Lock, LockOpen, Maximize2, Minimize2,
  MoreHorizontal, Pin, Printer, RotateCcw, Search, Share2, Star, Tag as TagIcon, Trash2, X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import {
  addShortcut, addTagToNotes, createTag, setNoteLocked, setSetting, deleteNotesForever, duplicateNote, removeShortcut, restoreNotes, setNoteTags, trashNotes, updateNote,
} from "../db/actions";
import { apiFetch } from "../api";
import { db, onBeforeClose, useQuery, useRow, useSyncState } from "../db/client";
import type { Editor as TEditor } from "@tiptap/core";
import { GapCursor } from "@tiptap/pm/gapcursor";
import { Editor, type EditorHandle } from "./editor/Editor";
import { applyRemap, deleteRemoved, reconcileRefs, refsOf, type Refs } from "./editor/refs";
import { searchKey } from "./editor/search";
import { revealPos } from "./editor/folds";
import { fullDate, relativeDate } from "./format";
import { LocationDialog } from "./MoveDialog";
import { Confirm, Menu, useAnchor } from "./Menu";
import { usePrefs } from "./prefs";
import { recordOpen } from "./opens";
import { ExportDialog } from "./ExportLazy";
import { ShareDialog } from "./ShareDialog";

const SAVE_DELAY = 400;
/**
 * Pause before saving after the last key. Saving serializes the whole note on the main thread (~30–50 ms for the
 * biggest notes on a PC, ~200 ms on a phone), so big notes wait a little longer between saves while you type.
 */
const saveDelay = (docSize: number) => (docSize > 60_000 ? 1500 : docSize > 20_000 ? 800 : SAVE_DELAY);

/**
 * Caret at the start of the body (Enter in the title, Enter in the note list). When the note starts
 * with an image, file or task, use a gap cursor before it: a text selection there is invalid and a
 * node selection would let the first keystroke replace the attachment.
 */
function caretAtStart(ed: TEditor) {
  ed.view.focus(); // synchronous; commands.focus() waits for a frame
  if (ed.state.doc.firstChild?.isTextblock) ed.commands.setTextSelection(1);
  else ed.view.dispatch(ed.state.tr.setSelection(new GapCursor(ed.state.doc.resolve(0))));
}

/** lastHtml after a failed save: matches no real text, so the retry always writes. */
const FAILED = "\u0000save failed";

/** Memoized: its list re-renders on every refresh (sync, saves), and the note's props rarely change. */
export const NoteView = memo(function NoteView({
  id,
  terms,
  onGone,
  onBack,
  onTrashed,
}: {
  id: string;
  terms: string[];
  onGone: () => void;
  onBack?: () => void;
  /** Where to go once the note is in the Trash (NotesPane: back on a phone, the next note on desktop). */
  onTrashed?: () => void;
}) {
  const note = useRow<Note>("SELECT * FROM notes WHERE id = ?", [id], ["notes"]);
  const notebook = useRow<Notebook>(note ? "SELECT * FROM notebooks WHERE id = ?" : null, [note?.notebook_id], ["notebooks"]);
  const prefs = usePrefs();
  const [, navigate] = useLocation();
  const editorRef = useRef<EditorHandle>(null);
  const [focused, setFocused] = useState(false);
  // Evernote swaps the "Edited …" line for the formatting toolbar once you start editing a note,
  // and keeps it until you click somewhere outside the note.
  const [editing, setEditing] = useState(false);
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!editing) return;
    const down = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".editor-pane, .popover, .modal-backdrop, .table-ctl, .block-handle")) setEditing(false);
    };
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, [editing]);
  const [dirty, setDirty] = useState(false);
  // A save that failed (the local database refused it): shown instead of "All changes saved", retried.
  const [saveError, setSaveError] = useState<string | null>(null);
  // false, or which bar: Ctrl+F opens Find, Ctrl+H opens Find & Replace (Evernote).
  const [findOpen, setFindOpen] = useState<false | "find" | "replace">(false);
  const [dialog, setDialog] = useState<null | "info" | "history" | "delete" | "export" | "share">(null);
  const [focusMode, setFocusMode] = useState(false);
  const menu = useAnchor();
  const [locate, setLocate] = useState<null | "move" | "copy">(null);

  // What we last wrote; lets us tell our own echo from a change made on another device.
  const lastHtml = useRef<string | null>(null);
  // The note version the editor's text is based on. Saves send it, so an edit that arrived by sync while the user
  // was typing (and so wasn't shown) makes a conflicting copy on the server instead of being overwritten.
  const baseVersion = useRef<number | undefined>(undefined);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingBody = useRef<(() => { html: string; text: string }) | null>(null);
  // Saves run one at a time; `saving` is the tail of that queue.
  const saving = useRef<Promise<void>>(Promise.resolve());
  const inFlight = useRef(0);
  // Every task/attachment placeholder this session has seen (see editor/refs.ts).
  const seen = useRef<Refs>({ task: [], att: [] });
  const remember = (html: string) => {
    const r = refsOf(html);
    seen.current.task.push(...r.task.filter((t) => !seen.current.task.includes(t)));
    seen.current.att.push(...r.att.filter((a) => !seen.current.att.includes(a)));
  };

  const flush = useCallback(() => {
    clearTimeout(saveTimer.current);
    const get = pendingBody.current;
    if (!get) return saving.current;
    pendingBody.current = null;
    const p = get();
    // Undo back to what's saved (or a no-op edit) writes nothing, so the note's date doesn't change.
    if (p.html === lastHtml.current && !inFlight.current) {
      setDirty(false);
      return saving.current;
    }
    lastHtml.current = p.html;
    inFlight.current++;
    saving.current = saving.current
      .then(async () => {
        // Placeholders brought back by undo/paste, or duplicated, get their own rows first.
        const { html, muts, remap } = await reconcileRefs(id, p.html);
        remember(html);
        if (muts.length) await db.mutate(muts);
        if (!pendingBody.current) lastHtml.current = html;
        await updateNote(id, { body_html: html, body_text: p.text }, baseVersion.current);
        const ed = editorRef.current?.editor;
        if (remap.length && ed) applyRemap(ed, remap);
      })
      .then(() => setSaveError(null))
      .catch((e) => {
        console.error("save failed", e);
        setSaveError(e instanceof Error ? e.message : String(e));
        // Keep the text for another try, unless newer typing already replaced it.
        lastHtml.current = FAILED; // never equal to real text, so the retry isn't skipped as "no change"
        if (!pendingBody.current) {
          pendingBody.current = get;
          clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(flush, 3000);
        }
      })
      .finally(() => {
        inFlight.current--;
        setDirty(!!pendingBody.current);
      });
    return saving.current;
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The session ends: save, then drop rows whose placeholders the user removed.
  const save = useCallback(
    () => flush().then(() => (lastHtml.current !== null ? deleteRemoved(id, seen.current, lastHtml.current) : undefined)),
    [flush, id],
  );
  const commit = useCallback(() => {
    // After a handover to another tab the database is gone; what was pending was saved first (db.saveAll).
    void save().catch((e) => db.closed || console.error("save failed", e));
  }, [save]);

  useEffect(() => () => commit(), [commit]);
  useEffect(() => onBeforeClose(save), [save]);
  useEffect(() => recordOpen(id), [id]);
  const pinnedHome = useRow<{ value: string }>("SELECT value FROM settings WHERE id = 'home.pinnedNote' AND deleted = 0", [], ["settings"]);
  const isPinnedHome = pinnedHome ? JSON.parse(pinnedHome.value) === id : false;
  useEffect(() => {
    const onHide = () => (document.visibilityState === "hidden" ? commit() : undefined);
    window.addEventListener("pagehide", commit);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", commit);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [commit]);

  // Apply edits that arrived by sync, unless the user is mid-edit.
  useEffect(() => {
    if (!note || lastHtml.current === null) {
      if (note) {
        lastHtml.current = note.body_html;
        baseVersion.current = note.version;
        remember(note.body_html);
      }
      return;
    }
    // Our own text, now acknowledged at a newer version (or unchanged): later edits build on that version.
    if (note.body_html === lastHtml.current) {
      if (!pendingBody.current && !inFlight.current) baseVersion.current = note.version;
      return;
    }
    const ed = editorRef.current?.editor;
    if (ed && !pendingBody.current && !inFlight.current) {
      lastHtml.current = note.body_html;
      baseVersion.current = note.version;
      remember(note.body_html);
      const { from, to } = ed.state.selection;
      // Not undoable: Ctrl+Z must not revert another device's edit.
      ed.chain().setMeta("addToHistory", false).setContent(note.body_html, { emitUpdate: false }).run();
      const max = ed.state.doc.content.size;
      ed.commands.setTextSelection({ from: Math.min(from, max), to: Math.min(to, max) });
    }
  }, [note?.body_html, note?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (note === null || note?.deleted) onGone();
  }, [note, onGone]);

  // Note shortcuts (Evernote): find, rename, move, copy link, note info; Enter in the list focuses the body.
  const live = useRef({ trashed: false });
  live.current = { trashed: note?.trashed_at != null };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (ctrl && !e.altKey && (k === "f" || k === "h")) {
        e.preventDefault();
        setFindOpen((open) => (k === "h" ? "replace" : open || "find"));
        setTimeout(() => document.querySelector<HTMLInputElement>(".find-bar input")?.select(), 0);
      } else if (ctrl && e.altKey && e.code === "KeyL") {
        e.preventDefault();
        void navigator.clipboard.writeText(`${location.origin}/note/${id}`);
      } else if ((ctrl && e.altKey && e.code === "KeyI") || (ctrl && e.shiftKey && k === "i")) {
        e.preventDefault();
        setDialog("info");
      } else if (ctrl && e.shiftKey && !e.altKey && k === "m" && !live.current.trashed) {
        e.preventDefault();
        setLocate("move");
      } else if (e.key === "F2" && !live.current.trashed) {
        e.preventDefault();
        document.querySelector<HTMLTextAreaElement>(".note-title")?.focus();
      }
    };
    const focusBody = () => {
      const ed = editorRef.current?.editor;
      if (!ed || live.current.trashed) return;
      caretAtStart(ed);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("engram:focus-editor", focusBody);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("engram:focus-editor", focusBody);
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const shortcut = useQuery<{ id: string }>("SELECT id FROM shortcuts WHERE kind = 'note' AND target_id = ? AND deleted = 0", [id], ["shortcuts"]);
  const isShortcut = !!shortcut?.length;
  const backlinks = useQuery<{ id: string; title: string }>(
    // CROSS JOIN keeps note_links first (its to_id index); a plain JOIN let the planner walk every note instead.
    "SELECT n.id, n.title FROM note_links l CROSS JOIN notes n ON n.id = l.from_id WHERE l.to_id = ? AND n.deleted = 0 AND n.trashed_at IS NULL ORDER BY n.updated DESC",
    [id],
    ["notes"],
  );

  if (!note) return <div className="editor-pane" />;
  const trashed = note.trashed_at != null;
  // Edit protection: a locked note reads like a trashed one (no edits), but keeps its menu.
  const locked = !trashed && note.locked_at != null;
  const readOnly = trashed || locked;

  return (
    <article
      className={`editor-pane${focusMode ? " focus-mode" : ""}`}
      onFocus={(e) => !readOnly && (e.target as HTMLElement).closest(".editor-scroll, .editor-chrome") && setEditing(true)}
    >
      <header className="editor-top">
        {onBack ? (
          <button className="icon-btn back" title="Back" aria-label="Back" onClick={onBack}>
            <ChevronLeft size={22} />
          </button>
        ) : (
          <button className="icon-btn" title={prefs.listCollapsed ? "Show note list" : "Hide note list"} onClick={() => prefs.set({ listCollapsed: !prefs.listCollapsed })}>
            {prefs.listCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        )}
        {!onBack && <button
          className="icon-btn"
          title={focusMode ? "Exit full screen" : "Expand note"}
          onClick={() => {
            setFocusMode(!focusMode);
            prefs.set({ sidebarCollapsed: !focusMode, listCollapsed: !focusMode });
          }}
        >
          {focusMode ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>}
        {onBack && (
          <>
            <button className="icon-btn" title="Undo" aria-label="Undo" disabled={readOnly} onClick={() => editorRef.current?.editor?.commands.undo()}><Undo2 size={18} /></button>
            <button className="icon-btn" title="Redo" aria-label="Redo" disabled={readOnly} onClick={() => editorRef.current?.editor?.commands.redo()}><Redo2 size={18} /></button>
          </>
        )}
        <div className="breadcrumb">
          {trashed ? (
            <button className="crumb" onClick={() => navigate(`/trash?n=${id}`)}>
              <Trash2 size={14} />
              <span>Trash</span>
            </button>
          ) : (
            <>
          <button className="crumb" onClick={() => navigate(`/notebook/${note.notebook_id}?n=${id}`)} title={`Go to ${notebook?.name ?? "notebook"}`} disabled={trashed}>
            <Book size={14} />
            <span>{notebook?.name ?? "…"}</span>
          </button>
          <ChevronRight size={13} className="crumb-sep" />
          <span className="crumb-note">{note.title || "Untitled"}</span>
            </>
          )}
        </div>
        <div className="editor-top-actions">
          <button className="icon-btn" title="More actions" onClick={menu.open}>
            <MoreHorizontal size={18} />
          </button>
        </div>
      </header>


      {findOpen && editorRef.current?.editor && <FindBar editorHandle={editorRef.current} terms={terms} replace={findOpen === "replace"} readOnly={readOnly} onClose={() => setFindOpen(false)} />}

      <div className={`editor-chrome${editing ? " editing" : ""}`}>
        {trashed && (
          <span className="chip trash-chip">
            <Trash2 size={12} /> Note in Trash
          </span>
        )}
        {locked && (
          <span className="chip lock-chip">
            <Lock size={12} /> Locked
            <button className="chip-btn" onClick={() => void setNoteLocked(id, false)}>
              Unlock to edit
            </button>
          </span>
        )}
        <div className="edited-line" title={fullDate(note.updated)}>Edited {relativeDate(note.updated).replace(/^Just now$/, "just now")}</div>
        <div className="toolbar-slot" ref={setToolbarSlot} />
      </div>

      <div
        className="editor-scroll"
        onMouseDown={(e) => {
          if (readOnly) return;
          setEditing(true);
          // Evernote: a click in the blank space below the note puts the caret at its end.
          const ed = editorRef.current?.editor;
          const dom = ed?.view.dom as HTMLElement | undefined;
          if (!ed || !dom || !ed.isEditable || dom.contains(e.target as Node) || e.clientY <= dom.getBoundingClientRect().bottom) return;
          e.preventDefault();
          if (ed.state.doc.lastChild?.isTextblock === false) ed.chain().insertContentAt(ed.state.doc.content.size, { type: "paragraph" }).focus("end").run();
          else ed.commands.focus("end");
        }}
      >
        {backlinks && backlinks.length > 0 && (
          <div className="backlinks">
            <span className="backlinks-label">Linked from</span>
            {backlinks.map((b) => (
              <button key={b.id} className="chip link" onClick={() => navigate(`/note/${b.id}`)}>
                {b.title || "Untitled"}
              </button>
            ))}
          </div>
        )}
        <TitleInput
          note={note}
          readOnly={readOnly}
          onEnter={() => {
            const ed = editorRef.current?.editor;
            if (!ed) return;
            caretAtStart(ed);
          }}
        />
        <Editor
          ref={editorRef}
          noteId={id}
          initialHtml={note.body_html}
          editable={!readOnly}
          terms={terms}
          collapsible={prefs.collapsibleSections}
          toolbarSlot={toolbarSlot}
          onFocusChange={setFocused}
          onChange={(body) => {
            if (readOnly) return; // a locked or trashed note is never saved, whatever changed its view
            pendingBody.current = body;
            setDirty(true);
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(flush, saveDelay(editorRef.current?.editor?.state.doc.content.size ?? 0));
          }}
        />
      </div>

      <footer className={`editor-bottom${focused ? " focused" : ""}`}>
        <TagBar note={note} readOnly={readOnly} />
        <SaveStatus dirty={dirty} saveError={saveError} />
      </footer>

      {locate && (
        <LocationDialog
          title={locate === "move" ? "Move" : "Copy to"}
          subject={note.title}
          current={locate === "move" ? note.notebook_id : undefined}
          onClose={() => setLocate(null)}
          onPick={async (nb) => {
            if (locate === "move") return void updateNote(id, { notebook_id: nb });
            const n = await duplicateNote(id, nb);
            if (n) navigate(`/note/${n}`);
          }}
        />
      )}
      {menu.anchor && (
        <Menu
          anchor={menu.anchor}
          align="end"
          onClose={menu.close}
          entries={
            trashed
              ? [
                  { label: "Restore note", icon: <RotateCcw size={14} />, onSelect: () => void restoreNotes([id]) },
                  { label: "Delete permanently", icon: <Trash2 size={14} />, danger: true, onSelect: () => setDialog("delete") },
                  { separator: true },
                  { label: "Print", icon: <Printer size={14} />, hint: "Ctrl+P", onSelect: () => window.print() },
                ]
              : [
                  { label: "Share…", icon: <Share2 size={14} />, onSelect: () => setDialog("share") },
                  { label: "Copy link", icon: <Link2 size={14} />, hint: "Ctrl+Alt+L", onSelect: () => void navigator.clipboard.writeText(`${location.origin}/note/${id}`) },
                  { separator: true },
                  { label: "Move…", icon: <FolderInput size={14} />, hint: "Ctrl+Shift+M", onSelect: () => setLocate("move") },
                  { label: "Copy to…", icon: <Copy size={14} />, onSelect: () => setLocate("copy") },
                  { label: "Duplicate", icon: <CopyPlus size={14} />, onSelect: async () => { const n = await duplicateNote(id); if (n) navigate(`/note/${n}`); } },
                  { label: "Edit tags", icon: <TagIcon size={14} />, onSelect: () => setTimeout(() => document.querySelector<HTMLInputElement>(".tag-input")?.focus(), 0) },
                  { separator: true },
                  { label: isShortcut ? "Remove from Shortcuts" : "Add to Shortcuts", icon: <Star size={14} />, onSelect: () => void (isShortcut ? removeShortcut("note", id) : addShortcut("note", id)) },
                  { label: note.pinned_at ? "Unpin from notebook" : "Pin to notebook", icon: <Pin size={14} />, onSelect: () => void updateNote(id, { pinned_at: note.pinned_at ? null : Date.now() }) },
                  { label: isPinnedHome ? "Unpin from Home" : "Pin to Home", icon: <Home size={14} />, onSelect: () => void setSetting("home.pinnedNote", isPinnedHome ? null : id) },
                  { separator: true },
                  { label: locked ? "Unlock note" : "Lock note", icon: locked ? <LockOpen size={14} /> : <Lock size={14} />, onSelect: () => void setNoteLocked(id, !locked) },
                  { label: "Collapsible sections", icon: <ChevronsDownUp size={14} />, checked: prefs.collapsibleSections, onSelect: () => prefs.set({ collapsibleSections: !prefs.collapsibleSections }) },
                  { label: "Find in note", icon: <Search size={14} />, hint: "Ctrl+F", onSelect: () => setFindOpen("find") },
                  { label: "Note info", icon: <Info size={14} />, hint: "Ctrl+Alt+I", onSelect: () => setDialog("info") },
                  { label: "Note history", icon: <History size={14} />, onSelect: () => setDialog("history") },
                  { label: "Print", icon: <Printer size={14} />, hint: "Ctrl+P", onSelect: () => window.print() },
                  { label: "Export…", icon: <Download size={14} />, onSelect: () => setDialog("export") },
                  { separator: true },
                  { label: "Move to Trash", icon: <Trash2 size={14} />, danger: true, onSelect: () => void trashNotes([id]).then(onTrashed) },
                ]
          }
        />
      )}
      {dialog === "share" && <ShareDialog noteId={id} title={note.title} onClose={() => setDialog(null)} />}
      {dialog === "export" && <ExportDialog scope={{ kind: "note", id, name: note.title }} onClose={() => setDialog(null)} />}
      {dialog === "info" && <NoteInfo note={note} notebook={notebook ?? undefined} onClose={() => setDialog(null)} />}
      {dialog === "history" && <NoteHistory note={note} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <Confirm title="Delete permanently?" body="This note will be deleted forever. This can't be undone." confirm="Delete" danger onConfirm={() => void deleteNotesForever([id])} onClose={() => setDialog(null)} />
      )}
    </article>
  );
});

/** "All changes saved" etc. Its own component: sync state changes (pending, syncing, idle) re-render only this. */
function SaveStatus({ dirty, saveError }: { dirty: boolean; saveError: string | null }) {
  const s = useSyncState();
  const text = saveError
    ? "Not saved yet, retrying…"
    : dirty
      ? "Saving…"
      : s.status === "offline"
        ? "Saved on this device"
        : s.status === "syncing" || s.pending > 0
          ? "Syncing…"
          : s.status === "error"
            ? "Sync error"
            : "All changes saved";
  return (
    <span className={`save-status${saveError ? " bad" : ""}`} title={saveError ?? undefined}>
      {text}
    </span>
  );
}

const FIELD_SIZING = typeof CSS !== "undefined" && CSS.supports("field-sizing", "content");

function TitleInput({ note, readOnly, onEnter }: { note: Note; readOnly: boolean; onEnter: () => void }) {
  const [v, setV] = useState(note.title);
  const ref = useRef<HTMLTextAreaElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const editing = useRef(false);
  // The version the shown title came from (see NoteView's baseVersion).
  const base = useRef(note.version);
  useEffect(() => {
    if (!editing.current) {
      setV(note.title);
      base.current = note.version;
    }
  }, [note.title, note.version]);
  useEffect(() => {
    const el = ref.current;
    if (!el || FIELD_SIZING) return;
    el.style.height = "0";
    el.style.height = `${el.scrollHeight}px`;
  }, [v]);
  useEffect(() => {
    // New empty notes start with the cursor in the title, like Evernote.
    if (!note.title && !note.body_text && !readOnly) ref.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // A title typed but not saved yet (300 ms debounce); saved at once before a handover to another tab.
  const pendingTitle = useRef<string | null>(null);
  const writeTitle = () => {
    clearTimeout(timer.current);
    const title = pendingTitle.current;
    pendingTitle.current = null;
    editing.current = false;
    return title !== null && title !== note.title ? updateNote(note.id, { title }, base.current) : Promise.resolve();
  };
  const writeRef = useRef(writeTitle);
  writeRef.current = writeTitle;
  useEffect(() => onBeforeClose(() => writeRef.current()), []);
  const save = (title: string) => {
    clearTimeout(timer.current);
    pendingTitle.current = title;
    timer.current = setTimeout(() => void writeTitle(), 300);
  };
  return (
    <textarea
      ref={ref}
      className="note-title"
      rows={1}
      maxLength={1000}
      placeholder="Title"
      value={v}
      readOnly={readOnly}
      onChange={(e) => {
        const t = e.target.value.replace(/\n/g, "");
        editing.current = true;
        setV(t);
        save(t);
      }}
      onKeyDown={(e) => {
        // Hangul (and other IME) composition sends Enter to finish the syllable: leave that to the IME.
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        if (e.key === "Enter" || (e.key === "ArrowDown" && ref.current?.selectionStart === v.length)) {
          e.preventDefault();
          onEnter();
        }
      }}
    />
  );
}

function TagBar({ note, readOnly }: { note: Note; readOnly: boolean }) {
  const all = useQuery<Tag>("SELECT * FROM tags WHERE deleted = 0 ORDER BY name COLLATE NOCASE", [], ["tags"]);
  const ids: string[] = useMemo(() => parseTagIds(note.tag_ids), [note.tag_ids]);
  const [, navigate] = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [navigated, setNavigated] = useState(false); // arrows moved the highlight, so Enter may pick from an empty box
  const inputRef = useRef<HTMLInputElement>(null);
  const byId = new Map(all?.map((t) => [t.id, t]));
  const matches = (all ?? []).filter((t) => !ids.includes(t.id) && t.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  const exact = all?.some((t) => t.name.toLowerCase() === q.trim().toLowerCase());
  const options = [...matches.map((t) => ({ id: t.id, label: t.name })), ...(q.trim() && !exact ? [{ id: "", label: `Create tag "${q.trim()}"` }] : [])];

  const add = async (opt: { id: string; label: string }) => {
    const name = q.trim();
    setQ("");
    setHi(0);
    setNavigated(false);
    // Read the note's tags at write time (not this render's), so quick successive adds all stick.
    await addTagToNotes([note.id], opt.id || (await createTag(name)));
  };

  return (
    <div className="tag-bar">
      <TagIcon size={14} className="muted" />
      {ids.map((t) =>
        byId.get(t) ? (
          <span key={t} className="chip">
            <button className="chip-label" onClick={() => navigate(`/tag/${t}`)}>{byId.get(t)!.name}</button>
            {!readOnly && (
              <button className="chip-x" aria-label="Remove tag" onClick={() => void setNoteTags(note.id, ids.filter((x) => x !== t))}>
                <X size={11} />
              </button>
            )}
          </span>
        ) : null,
      )}
      {!readOnly && (
        <span className="tag-add">
          <input
            ref={inputRef}
            className="tag-input"
            placeholder={ids.length ? "" : "Add tag"}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setHi(0);
              setNavigated(false);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing || e.keyCode === 229) return; // IME composing (Korean): not ours yet
              if (e.key === "ArrowDown") {
                setHi((h) => (navigated ? Math.min(h + 1, options.length - 1) : 0));
                setNavigated(true);
              } else if (e.key === "ArrowUp") setHi((h) => Math.max(h - 1, 0));
              else if ((e.key === "Enter" || e.key === ",") && options[hi] && (q.trim() || navigated)) {
                e.preventDefault();
                void add(options[hi]!);
              } else if (e.key === "Backspace" && !q && ids.length) void setNoteTags(note.id, ids.slice(0, -1));
              else if (e.key === "Escape") inputRef.current?.blur();
            }}
          />
          {open && options.length > 0 && (
            <div className="menu tag-suggest">
              {options.map((o, i) => (
                <button key={o.id || "new"} className={`menu-item${i === hi && (q.trim() || navigated) ? " active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => void add(o)}>
                  <span className="menu-label">{o.label}</span>
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
}

function FindBar({ editorHandle, terms, replace, readOnly, onClose }: { editorHandle: EditorHandle; terms: string[]; replace: boolean; readOnly: boolean; onClose: () => void }) {
  const editor = editorHandle.editor!;
  const [q, setQ] = useState("");
  const [rep, setRep] = useState("");
  // A locked or trashed note can be searched, never changed.
  const [showReplace, setShowReplace] = useState(replace && !readOnly);
  useEffect(() => {
    if (replace && !readOnly) setShowReplace(true);
    if (readOnly) setShowReplace(false);
  }, [replace, readOnly]);
  const [active, setActive] = useState(0);
  const [, force] = useState(0);
  const matches = searchKey.getState(editor.state)?.matches ?? [];

  useEffect(() => {
    editor.commands.setSearch(q ? [q] : [], q ? active : -1);
    const m = searchKey.getState(editor.state)?.matches[active];
    if (m) {
      revealPos(editor.view, m.from);
      const dom = editor.view.domAtPos(m.from).node as HTMLElement;
      (dom.nodeType === 3 ? dom.parentElement : dom)?.scrollIntoView({ block: "center" });
    }
    force((x) => x + 1);
  }, [q, active, editor]);

  // On close, put back the list search's highlights. Only on close: `terms` is a new array on every list refresh
  // (each save), and re-running this then wiped the Find query mid-use.
  const termsRef = useRef(terms);
  termsRef.current = terms;
  useEffect(() => () => void editor.commands.setSearch(termsRef.current), [editor]);

  const go = (d: number) => matches.length && setActive((a) => (a + d + matches.length) % matches.length);
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "g") {
        e.preventDefault();
        goRef.current(e.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const replaceOne = () => {
    const m = matches[active];
    if (!m || readOnly) return;
    editor.chain().insertContentAt({ from: m.from, to: m.to }, rep).run();
    // Move on to the first match after the new text ("cat" → "cats" must not match the same spot again).
    const after = m.from + rep.length;
    const next = (searchKey.getState(editor.state)?.matches ?? []).findIndex((x) => x.from >= after);
    setActive(next >= 0 ? next : 0);
    force((x) => x + 1);
  };
  const replaceAll = () => {
    if (readOnly) return;
    const tr = editor.state.tr;
    for (const m of [...matches].reverse()) tr.insertText(rep, m.from, m.to);
    editor.view.dispatch(tr);
    force((x) => x + 1);
  };

  return (
    <div className="find-bar" onKeyDown={(e) => e.key === "Escape" && onClose()}>
      <div className="find-row">
        <Search size={14} className="muted" />
        <input autoFocus placeholder="Find in note" value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={(e) => e.key === "Enter" && go(e.shiftKey ? -1 : 1)} />
        <span className="find-count">{q ? `${matches.length ? active + 1 : 0} of ${matches.length}` : ""}</span>
        <button className="icon-btn" title="Previous" onClick={() => go(-1)}><ChevronLeft size={15} /></button>
        <button className="icon-btn" title="Next" onClick={() => go(1)}><ChevronRight size={15} /></button>
        {!readOnly && <button className="btn small" onClick={() => setShowReplace(!showReplace)}>Replace</button>}
        <button className="icon-btn" title="Close" onClick={onClose}><X size={15} /></button>
      </div>
      {showReplace && !readOnly && (
        <div className="find-row">
          <Copy size={14} className="muted" style={{ visibility: "hidden" }} />
          <input placeholder="Replace with" value={rep} onChange={(e) => setRep(e.target.value)} onKeyDown={(e) => e.key === "Enter" && replaceOne()} />
          <button className="btn small" onClick={replaceOne} disabled={!matches.length}>Replace</button>
          <button className="btn small" onClick={replaceAll} disabled={!matches.length}>Replace all</button>
        </div>
      )}
    </div>
  );
}

function NoteInfo({ note, notebook, onClose }: { note: Note; notebook?: Notebook; onClose: () => void }) {
  const atts = useQuery<{ n: number; size: number }>("SELECT COUNT(*) AS n, COALESCE(SUM(size), 0) AS size FROM attachments WHERE note_id = ? AND deleted = 0", [note.id], ["attachments"])?.[0];
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>Note info</h2>
        <dl className="info-grid">
          <dt>Notebook</dt><dd>{notebook?.name}</dd>
          <dt>Created</dt><dd>{fullDate(note.created)}</dd>
          <dt>Updated</dt><dd>{fullDate(note.updated)}</dd>
          {note.source_url && (<><dt>Source URL</dt><dd><a href={note.source_url} target="_blank" rel="noreferrer">{note.source_url}</a></dd></>)}
          <dt>Words</dt><dd>{note.body_text.split(/\s+/).filter(Boolean).length.toLocaleString()}</dd>
          <dt>Characters</dt><dd>{note.body_text.length.toLocaleString()}</dd>
          <dt>Attachments</dt><dd>{atts ? `${atts.n} (${(atts.size / 1024 / 1024).toFixed(1)} MB)` : ""}</dd>
          <dt>Version</dt><dd>{note.version}</dd>
        </dl>
        <div className="modal-actions"><button className="btn" onClick={onClose}>Close</button></div>
      </div>
    </div>,
    document.body,
  );
}

interface Version {
  version: number;
  title: string;
  created: number;
}

function NoteHistory({ note, onClose }: { note: Note; onClose: () => void }) {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<{ v: Version; title: string; html: string } | null>(null);
  useEffect(() => {
    apiFetch(`/history/${note.id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { versions: Version[] }) => setVersions(j.versions))
      .catch(() => setError("Note history needs a connection to the server."));
  }, [note.id]);
  const open = async (v: Version) => {
    const r = await apiFetch(`/history/${note.id}/${v.version}`);
    if (!r.ok) return;
    const j = (await r.json()) as { title: string; body_html: string };
    setPreview({ v, title: j.title, html: j.body_html });
  };
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide history">
        <h2>Note history</h2>
        <div className="history-body">
          <div className="history-list">
            {error && <p className="muted">{error}</p>}
            {versions?.length === 0 && <p className="muted">No earlier versions yet. Versions are saved at most hourly while you edit.</p>}
            {versions?.map((v) => (
              <button key={v.version} className={`menu-item${preview?.v.version === v.version ? " active" : ""}`} onClick={() => void open(v)}>
                <span className="menu-label">{fullDate(v.created)}</span>
              </button>
            ))}
          </div>
          <div className="history-preview">
            {preview ? (
              <>
                <h3>{preview.title || "Untitled"}</h3>
                <div className="note-body readonly" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html) }} />
              </>
            ) : (
              <p className="muted">Select a version to preview it.</p>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Close</button>
          <button
            className="btn primary"
            disabled={!preview}
            onClick={async () => {
              if (!preview) return;
              // Tasks and files the old version refers to come back as rows of their own.
              const [{ html, muts }, { htmlToText }] = await Promise.all([reconcileRefs(note.id, preview.html), import("@engram/core/text")]);
              if (muts.length) await db.mutate(muts);
              await updateNote(note.id, { title: preview.title, body_html: html, body_text: htmlToText(html).normalize("NFC") });
              await db.sync();
              onClose();
            }}
          >
            Restore this version
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
